/* eslint-disable @typescript-eslint/no-unused-vars */
import { z } from 'zod/v4';
import { stringToBooleanSchema } from '../common/index';
import {
  gameTypeEnum as gameTypeSchema,
  lobbyStatusEnum as lobbyStatusSchema,
  lobbyPlayerStatusEnum as lobbyPlayerStatusSchema,
  gameSessionStatusEnum as gameSessionStatusSchema,
  playerReportCategoryEnum as playerReportCategorySchema,
  playerReportStatusEnum as playerReportStatusSchema,
  achievementCategoryEnum as achievementCategorySchema,
  achievementConditionTypeEnum as achievementConditionTypeSchema,
  type GameType,
  type LobbyStatus,
  type LobbyPlayerStatus,
  type GameSessionStatus,
  type PlayerReportCategory,
  type PlayerReportStatus,
  type AchievementCategory,
  type AchievementConditionType,
} from '../enums/index';

// ==========================================
// LOBBY SCHEMAS
// ==========================================

export const createLobbySchema = z.object({
  gameId: z.number({ message: 'ID игры обязателен' })
    .int('ID игры должен быть целым числом')
    .positive('ID игры должен быть положительным'),
  name: z.string().max(100, 'Название лобби слишком длинное (максимум 100 символов)').optional(),
  password: z.string().max(50, 'Пароль слишком длинный (максимум 50 символов)').optional(),
  isPrivate: z.boolean().optional(),
  maxPlayers: z.number().int('Максимум игроков должен быть целым числом').positive('Максимум игроков должен быть положительным').optional(),
  settings: z.record(z.string(), z.unknown()).optional(),
});

// Schema for creating lobby from specific game endpoint (gameId is determined by URL)
export const createLobbyBodySchema = z.object({
  name: z.string().max(100, 'Название лобби слишком длинное (максимум 100 символов)').optional(),
  password: z.string().max(50, 'Пароль слишком длинный (максимум 50 символов)').optional(),
  isPrivate: z.boolean().optional(),
  maxPlayers: z.number().int('Максимум игроков должен быть целым числом').positive('Максимум игроков должен быть положительным').optional(),
  settings: z.record(z.string(), z.unknown()).optional(),
}).default({});

export const joinLobbyBodySchema = z.object({
  password: z.string().max(50, 'Пароль слишком длинный').optional(),
}).default({});

export const matchmakingBodySchema = z.object({
  language: z.string().max(10, 'Код языка слишком длинный').optional(),
}).default({});

export const lobbiesFilterSchema = z.object({
  gameId: z.coerce.number().int('ID игры должен быть целым числом').optional(),
  status: lobbyStatusSchema.optional(),
  language: z.string().optional(),
  page: z.coerce.number().int('Страница должна быть целым числом').min(1, 'Страница должна быть >= 1').default(1).optional(),
  limit: z.coerce.number().int('Лимит должен быть целым числом').min(1, 'Лимит должен быть >= 1').max(100, 'Лимит должен быть <= 100').default(20).optional(),
});

// ==========================================
// GAME ACTIONS
// ==========================================

export const cardColorGuessSchema = z.object({
  color: z.enum(['WHITE', 'BLACK'], { message: 'Цвет обязателен' }),
});

export const guessNumberSetSchema = z.object({
  number: z.number({ message: 'Число обязательно' })
    .int('Число должно быть целым')
    .min(1, 'Число должно быть от 1 до 999999')
    .max(999999, 'Число должно быть от 1 до 999999'),
});

export const guessNumberGuessSchema = z.object({
  guess: z.string({ message: 'Догадка обязательна' })
    .min(1, 'Догадка не может быть пустой')
    .max(200, 'Догадка слишком длинная (максимум 200 символов)'),
});

export const markCorrectSchema = z.object({
  guessId: z.number({ message: 'ID догадки обязателен' })
    .int('ID догадки должен быть целым числом'),
});

// ==========================================
// REPORTS
// ==========================================

export const createGameReportSchema = z.object({
  reportedUserId: z.number({ message: 'ID пользователя обязателен' })
    .int('ID пользователя должен быть целым числом'),
  sessionId: z.number().int('ID сессии должен быть целым числом').optional(),
  lobbyId: z.number().int('ID лобби должен быть целым числом').optional(),
  category: playerReportCategorySchema,
  description: z.string().max(1000, 'Описание слишком длинное (максимум 1000 символов)').optional(),
});

// ==========================================
// MODERATION SCHEMAS
// ==========================================

export const moderationLobbyFiltersSchema = z.object({
  gameId: z.coerce.number().int('ID игры должен быть целым числом').optional(),
  status: lobbyStatusSchema.optional(),
  hostUserId: z.coerce.number().int('ID хоста должен быть целым числом').optional(),
  isPrivate: stringToBooleanSchema,
  page: z.coerce.number().int('Страница должна быть целым числом').min(1, 'Страница должна быть >= 1').default(1).optional(),
  pageSize: z.coerce.number().int('Размер страницы должен быть целым числом').min(1, 'Размер страницы должен быть >= 1').max(100, 'Размер страницы должен быть <= 100').default(20).optional(),
});

export const moderationSessionFiltersSchema = z.object({
  gameId: z.coerce.number().int('ID игры должен быть целым числом').optional(),
  status: gameSessionStatusSchema.optional(),
  lobbyId: z.coerce.number().int('ID лобби должен быть целым числом').optional(),
  page: z.coerce.number().int('Страница должна быть целым числом').min(1, 'Страница должна быть >= 1').default(1).optional(),
  pageSize: z.coerce.number().int('Размер страницы должен быть целым числом').min(1, 'Размер страницы должен быть >= 1').max(100, 'Размер страницы должен быть <= 100').default(20).optional(),
});

export const closeLobbySchema = z.object({
  reason: z.string({ message: 'Причина обязательна' })
    .min(1, 'Причина обязательна')
    .max(500, 'Причина слишком длинная (максимум 500 символов)'),
});

export const cancelSessionSchema = z.object({
  reason: z.string({ message: 'Причина обязательна' })
    .min(1, 'Причина обязательна')
    .max(500, 'Причина слишком длинная (максимум 500 символов)'),
});

export const kickPlayerSchema = z.object({
  reason: z.string({ message: 'Причина обязательна' })
    .min(1, 'Причина обязательна')
    .max(500, 'Причина слишком длинная (максимум 500 символов)'),
});

export const banUserSchema = z.object({
  reason: z.string({ message: 'Причина обязательна' })
    .min(1, 'Причина обязательна')
    .max(500, 'Причина слишком длинная (максимум 500 символов)'),
  durationMinutes: z.number().int('Длительность должна быть целым числом').min(1, 'Длительность должна быть >= 1 минуты').optional(),
});

export const unbanUserSchema = z.object({
  reason: z.string().max(500, 'Причина слишком длинная (максимум 500 символов)').optional(),
});

// ==========================================
// ENUM TYPES (imported from ../enums/index)
// ==========================================

// Re-export for backward compatibility
export type {
  GameType,
  LobbyStatus,
  LobbyPlayerStatus,
  GameSessionStatus,
  PlayerReportCategory,
  PlayerReportStatus,
  AchievementCategory,
  AchievementConditionType,
};

// ==========================================
// GAME STATE TYPES
// ==========================================

export interface CardColorGameState {
  cardColor: 'WHITE' | 'BLACK';
  userGuess: 'WHITE' | 'BLACK' | null;
  result: 'WIN' | 'LOSE' | null;
  score: number;
}

export interface GuessNumberGameState {
  phase: 'PICKING_NUMBER' | 'GUESSING' | 'FINISHED';
  pickerUserId: number;
  secretNumber: number | null;
  currentTurnUserId: number | null;
  turnOrder: number[];
  guesses: {
    orderId: number;
    id?: number;
    userId: number;
    guess: string;
    isCorrect: boolean | null;
    createdAt: string;
  }[];
  winnerId: number | null;
  startedAt: string;
  expiresAt: string;
}

export type GameState = CardColorGameState | GuessNumberGameState;

// ==========================================
// CONDITION TYPES
// ==========================================

export interface AchievementCondition {
  type: AchievementConditionType;
  metric: string;
  target: number;
  gameId?: number;
}

// ==========================================
// DATABASE MODEL TYPES (from Drizzle)
// ==========================================

// ВАЖНО: Используем InferSelectModel из Drizzle для автоматической типизации
// Эти типы должны быть импортированы из @masters-club/db, но мы не можем это сделать
// из-за циклической зависимости. Поэтому здесь мы определяем базовые типы.
// Если нужны точные типы из БД, используйте типы напрямую в репозиториях.

export interface Game {
  id: number;
  type: GameType;
  name: string;
  description: string | null;
  minPlayers: number;
  maxPlayers: number;
  settings: Record<string, unknown> | null;
  isActive: boolean;
  createdAt: Date;
  updatedAt: Date;
}

export interface GameLobby {
  id: number;
  gameId: number;
  hostUserId: number;
  name: string | null;
  password: string | null;
  isPrivate: boolean;
  language: string;
  status: LobbyStatus;
  maxPlayers: number;
  settings: Record<string, unknown> | null;
  chatRoomId: number | null;
  expiresAt: Date | null;
  createdAt: Date;
  updatedAt: Date;
}

export interface LobbyPlayer {
  id: number;
  lobbyId: number;
  userId: number;
  status: LobbyPlayerStatus;
  isReady: boolean;
  turnOrder: number | null;
  score: number;
  joinedAt: Date;
  leftAt: Date | null;
  kickedByAdminId: number | null;
  kickReason: string | null;
}

export interface GameSession {
  id: number;
  lobbyId: number | null;
  gameId: number;
  status: GameSessionStatus;
  gameState: GameState;
  currentTurnUserId: number | null;
  turnNumber: number;
  winnerId: number | null;
  startedAt: Date;
  finishedAt: Date | null;
  createdAt: Date;
  updatedAt: Date;
}

export interface GameMove {
  id: number;
  sessionId: number;
  userId: number;
  moveType: string;
  moveData: Record<string, unknown>;
  result: Record<string, unknown> | null;
  createdAt: Date;
}

export interface Achievement {
  id: number;
  code: string;
  name: string;
  description: string | null;
  iconUrl: string | null;
  category: AchievementCategory;
  gameId: number | null;
  conditionType: AchievementConditionType;
  condition: AchievementCondition;
  rewardPoints: number;
  isActive: boolean;
  createdAt: Date;
  updatedAt: Date;
}

export interface UserGameStats {
  id: number;
  userId: number;
  gameId: number;
  totalGames: number;
  wins: number;
  losses: number;
  draws: number;
  totalScore: number;
  bestScore: number;
  avgScore: string;
  currentStreak: number;
  bestStreak: number;
  createdAt: Date;
  updatedAt: Date;
}

export interface PlayerReport {
  id: number;
  reporterUserId: number;
  reportedUserId: number;
  sessionId: number | null;
  lobbyId: number | null;
  category: PlayerReportCategory;
  description: string | null;
  status: PlayerReportStatus;
  reviewedByAdminId: number | null;
  reviewedAt: Date | null;
  resolution: string | null;
  createdAt: Date;
  updatedAt: Date;
}

export interface UserAchievement {
  id: number;
  userId: number;
  achievementId: number;
  progress: number;
  isUnlocked: boolean;
  unlockedAt: Date | null;
  createdAt: Date;
  updatedAt: Date;
}

// ==========================================
// RESPONSE TYPES
// ==========================================

export interface GameResponse {
  id: number;
  type: GameType;
  name: string;
  description: string | null;
  minPlayers: number;
  maxPlayers: number;
  settings: Record<string, unknown> | null;
  isActive: boolean;
}

export interface LobbyPlayerResponse {
  id: number;
  lobbyId: number;
  userId: number;
  status: LobbyPlayerStatus;
  isReady: boolean;
  turnOrder: number | null;
  score: number;
  joinedAt: Date;
  user: {
    id: number;
    firstName: string | null;
    lastName: string | null;
    username: string | null;
    photoUrl: string | null;
  };
}

export interface LobbyResponse {
  id: number;
  gameId: number;
  hostUserId: number;
  name: string | null;
  isPrivate: boolean;
  language: string;
  status: LobbyStatus;
  maxPlayers: number;
  settings: Record<string, unknown> | null;
  chatRoomId: number | null;
  expiresAt: Date | null;
  createdAt: Date;
  players: LobbyPlayerResponse[];
  playersCount: number;
  game?: GameResponse;
}

export interface GameSessionResponse {
  id: number;
  lobbyId: number | null;
  gameId: number;
  status: GameSessionStatus;
  gameState: GameState;
  currentTurnUserId: number | null;
  turnNumber: number;
  winnerId: number | null;
  startedAt: Date;
  finishedAt: Date | null;
}

export interface GameMoveResponse {
  id: number;
  sessionId: number;
  userId: number;
  moveType: string;
  moveData: Record<string, unknown>;
  result: Record<string, unknown> | null;
  createdAt: Date;
  user?: {
    id: number;
    firstName: string | null;
    username: string | null;
  };
}

export interface AchievementResponse {
  id: number;
  code: string;
  name: string;
  description: string | null;
  iconUrl: string | null;
  category: AchievementCategory;
  gameId: number | null;
  rewardPoints: number;
  progress: number;
  target: number;
  isUnlocked: boolean;
  unlockedAt: Date | null;
}

export interface UserGameStatsResponse {
  id: number;
  userId: number;
  gameId: number;
  totalGames: number;
  wins: number;
  losses: number;
  draws: number;
  totalScore: number;
  bestScore: number;
  avgScore: number;
  currentStreak: number;
  bestStreak: number;
}

export interface PlayerReportResponse {
  id: number;
  reporterUserId: number;
  reportedUserId: number;
  sessionId: number | null;
  lobbyId: number | null;
  category: PlayerReportCategory;
  description: string | null;
  status: PlayerReportStatus;
  createdAt: Date;
}

// ==========================================
// SERVICE TYPES
// ==========================================

export interface UserInfo {
  id: number;
  firstName: string | null;
  lastName: string | null;
  username: string | null;
  photoUrl: string | null;
  language?: string;
}

export interface AchievementUnlockedEvent {
  userId: number;
  achievement: AchievementResponse;
  unlockedAt: Date;
}

// ==========================================
// MODERATION TYPES
// ==========================================

export interface LobbyFilters {
  gameId?: number;
  status?: LobbyStatus;
  hostUserId?: number;
  isPrivate?: boolean;
  page?: number;
  pageSize?: number;
}

export interface SessionFilters {
  gameId?: number;
  status?: GameSessionStatus;
  lobbyId?: number;
  page?: number;
  pageSize?: number;
}

export interface LobbyWithStats {
  id: number;
  gameId: number;
  gameName: string;
  gameType: string;
  hostUserId: number;
  hostUsername: string | null;
  hostFirstName: string | null;
  name: string | null;
  isPrivate: boolean;
  language: string;
  status: string;
  maxPlayers: number;
  currentPlayers: number;
  createdAt: Date;
  expiresAt: Date | null;
}

export interface LobbyDetails {
  id: number;
  gameId: number;
  game: {
    id: number;
    type: string;
    name: string;
  };
  host: {
    id: number;
    firstName: string | null;
    lastName: string | null;
    username: string | null;
  };
  name: string | null;
  isPrivate: boolean;
  language: string;
  status: string;
  maxPlayers: number;
  settings: Record<string, unknown> | null;
  chatRoomId: number | null;
  expiresAt: Date | null;
  createdAt: Date;
  players: {
    id: number;
    lobbyId: number;
    userId: number;
    status: string;
    isReady: boolean;
    turnOrder: number | null;
    score: number;
    joinedAt: Date;
    leftAt: Date | null;
    kickedByAdminId: number | null;
    kickReason: string | null;
    user: {
      id: number;
      firstName: string | null;
      lastName: string | null;
      username: string | null;
    };
  }[];
  activeSession: {
    id: number;
    status: string;
    startedAt: Date;
    finishedAt: Date | null;
  } | null;
}

export interface SessionWithStats {
  id: number;
  lobbyId: number | null;
  gameId: number;
  gameName: string;
  gameType: string;
  status: string;
  turnNumber: number;
  winnerId: number | null;
  startedAt: Date;
  finishedAt: Date | null;
  movesCount: number;
}

export interface SessionDetails {
  id: number;
  lobbyId: number | null;
  gameId: number;
  game: {
    id: number;
    type: string;
    name: string;
  };
  status: string;
  gameState: Record<string, unknown>;
  currentTurnUserId: number | null;
  turnNumber: number;
  winnerId: number | null;
  winner: {
    id: number;
    firstName: string | null;
    username: string | null;
  } | null;
  startedAt: Date;
  finishedAt: Date | null;
  players: {
    userId: number;
    firstName: string | null;
    lastName: string | null;
    username: string | null;
    score: number;
  }[];
}

export interface GameModerationStats {
  activeLobbies: number;
  inProgressLobbies: number;
  finishedLobbies: number;
  activeSessions: number;
  totalPlayersInLobbies: number;
  lobbiesLast24h: number;
  sessionsLast24h: number;
}

// ==========================================
// WEBSOCKET EVENT TYPES
// ==========================================

// Client -> Server
export interface WsJoinLobby {
  type: 'join_lobby';
  lobbyId: number;
  password?: string;
}

export interface WsLeaveLobby {
  type: 'leave_lobby';
  lobbyId: number;
}

export interface WsSetReady {
  type: 'set_ready';
  lobbyId: number;
  isReady: boolean;
}

export interface WsStartGame {
  type: 'start_game';
  lobbyId: number;
}

export interface WsKickPlayerMsg {
  type: 'kick_player';
  lobbyId: number;
  userId: number;
}

export interface WsSetNumber {
  type: 'set_number';
  sessionId: number;
  number: number;
}

export interface WsMakeGuess {
  type: 'make_guess';
  sessionId: number;
  guess: string;
}

export interface WsMarkCorrectMsg {
  type: 'mark_correct';
  sessionId: number;
  guessId: number;
}

export interface WsCardGuess {
  type: 'card_guess';
  sessionId: number;
  color: 'WHITE' | 'BLACK';
}

export type WsGameClientMessage =
  | WsJoinLobby
  | WsLeaveLobby
  | WsSetReady
  | WsStartGame
  | WsKickPlayerMsg
  | WsSetNumber
  | WsMakeGuess
  | WsMarkCorrectMsg
  | WsCardGuess
  | WsSubscribeLobbies
  | WsUnsubscribeLobbies
  | WsCreateLobby
  | WsGetLobbies
  | WsMatchmaking
  | { type: 'pong' };

// Server -> Client
export interface WsLobbyJoined {
  type: 'lobby_joined';
  lobby: LobbyResponse;
}

export interface WsPlayerJoined {
  type: 'player_joined';
  lobbyId: number;
  player: LobbyPlayerResponse;
}

export interface WsPlayerLeft {
  type: 'player_left';
  lobbyId: number;
  userId: number;
}

export interface WsPlayerReady {
  type: 'player_ready';
  lobbyId: number;
  userId: number;
  isReady: boolean;
}

export interface WsGameStarting {
  type: 'game_starting';
  lobbyId: number;
  countdown: number;
}

export interface WsGameStarted {
  type: 'game_started';
  session: GameSessionResponse;
}

export interface WsPickNumber {
  type: 'pick_number';
  sessionId: number;
}

export interface WsNumberPicked {
  type: 'number_picked';
  sessionId: number;
}

export interface WsYourTurn {
  type: 'your_turn';
  sessionId: number;
}

export interface WsGuessMade {
  type: 'guess_made';
  sessionId: number;
  guessId: number;
  userId: number;
  guess: string;
}

export interface WsGuessResult {
  type: 'guess_result';
  sessionId: number;
  guessId: number;
  isCorrect: boolean;
}

export interface WsGameFinished {
  type: 'game_finished';
  session: GameSessionResponse;
  winner: {
    id: number;
    firstName: string | null;
    username: string | null;
  } | null;
  secretNumber?: number;
}

export interface WsGameTimeout {
  type: 'game_timeout';
  sessionId: number;
  secretNumber: number;
}

export interface WsCardResult {
  type: 'card_result';
  sessionId: number;
  actualColor: 'WHITE' | 'BLACK';
  guessedColor: 'WHITE' | 'BLACK';
  isCorrect: boolean;
  newScore: number;
}

export interface WsGameError {
  type: 'error';
  message: string;
  code?: string;
}

export interface WsLobbyCancelled {
  type: 'lobby_cancelled';
  lobbyId: number;
  reason: 'EMPTY' | 'HOST_LEFT' | 'EXPIRED' | 'MANUAL';
}

// Lobby list subscription events (Client -> Server)
export interface WsSubscribeLobbies {
  type: 'subscribe_lobbies';
  gameType: GameType;
}

export interface WsUnsubscribeLobbies {
  type: 'unsubscribe_lobbies';
}

// Create lobby via WebSocket (Client -> Server)
export interface WsCreateLobby {
  type: 'create_lobby';
  gameType: GameType;
  name?: string;
  password?: string;
  isPrivate?: boolean;
  maxPlayers?: number;
  settings?: Record<string, unknown>;
}

// Get lobbies list via WebSocket (Client -> Server)
export interface WsGetLobbies {
  type: 'get_lobbies';
  gameType: GameType;
  language?: string;
}

// Matchmaking request via WebSocket (Client -> Server)
export interface WsMatchmaking {
  type: 'matchmaking';
  gameType: GameType;
}

// Lobby list update events (Server -> Client)
export interface WsLobbyCreated {
  type: 'lobby_created';
  lobby: {
    id: number;
    gameId: number;
    hostUserId: number;
    name: string | null;
    isPrivate: boolean;
    language: string;
    status: LobbyStatus;
    maxPlayers: number;
    playersCount: number;
    createdAt: string;
  };
}

export interface WsLobbyUpdated {
  type: 'lobby_updated';
  lobbyId: number;
  updates: {
    status?: LobbyStatus;
    playersCount?: number;
  };
}

export interface WsLobbyRemoved {
  type: 'lobby_removed';
  lobbyId: number;
  reason: 'STARTED' | 'CANCELLED' | 'EXPIRED' | 'FULL';
}

export interface WsLobbiesSubscribed {
  type: 'lobbies_subscribed';
  gameType: GameType;
}

// Response for lobby creation (Server -> Client)
export interface WsLobbyCreatedSelf {
  type: 'lobby_created_self';
  lobby: LobbyResponse;
}

// Response for get_lobbies request (Server -> Client)
export interface WsLobbiesList {
  type: 'lobbies_list';
  lobbies: {
    id: number;
    gameId: number;
    hostUserId: number;
    name: string | null;
    isPrivate: boolean;
    language: string;
    status: LobbyStatus;
    maxPlayers: number;
    playersCount: number;
    createdAt: string;
  }[];
}

// Response for matchmaking (Server -> Client)
export interface WsMatchmakingResult {
  type: 'matchmaking_result';
  found: boolean;
  lobby?: LobbyResponse;
  message?: string;
}

export type WsGameServerMessage =
  | WsLobbyJoined
  | WsPlayerJoined
  | WsPlayerLeft
  | WsPlayerReady
  | WsGameStarting
  | WsGameStarted
  | WsPickNumber
  | WsNumberPicked
  | WsYourTurn
  | WsGuessMade
  | WsGuessResult
  | WsGameFinished
  | WsGameTimeout
  | WsCardResult
  | WsGameError
  | WsLobbyCancelled
  | WsLobbyCreated
  | WsLobbyUpdated
  | WsLobbyRemoved
  | WsLobbiesSubscribed
  | WsLobbyCreatedSelf
  | WsLobbiesList
  | WsMatchmakingResult
  | { type: 'ping' }
  | { type: 'connected'; userId: number };

// ==========================================
// DTO TYPES
// ==========================================

export type CreateLobbyDto = z.infer<typeof createLobbySchema>;
export type CreateLobbyBodyDto = z.infer<typeof createLobbyBodySchema>;
export type JoinLobbyDto = z.infer<typeof joinLobbyBodySchema>;
export type LobbiesFilterDto = z.infer<typeof lobbiesFilterSchema>;
export type CardColorGuessDto = z.infer<typeof cardColorGuessSchema>;
export type GuessNumberSetDto = z.infer<typeof guessNumberSetSchema>;
export type GuessNumberGuessDto = z.infer<typeof guessNumberGuessSchema>;
export type MarkCorrectDto = z.infer<typeof markCorrectSchema>;
export type CreateGameReportDto = z.infer<typeof createGameReportSchema>;
export type ModerationLobbyFiltersDto = z.infer<typeof moderationLobbyFiltersSchema>;
export type ModerationSessionFiltersDto = z.infer<typeof moderationSessionFiltersSchema>;
export type CloseLobbyDto = z.infer<typeof closeLobbySchema>;
export type CancelSessionDto = z.infer<typeof cancelSessionSchema>;
export type KickPlayerDto = z.infer<typeof kickPlayerSchema>;
export type BanUserDto = z.infer<typeof banUserSchema>;
export type UnbanUserDto = z.infer<typeof unbanUserSchema>;
